import React, { useState } from 'react';
import {
  Accordion,
  AccordionHeader,
  Typography,
  AccordionBody,
  TextField,
  FormStatusMessage,
  Button,
} from '@one-thd/sui-atomic-components';

const TOTAL_AMOUNT_ACCORDION_CONTENT_ID = 'order-filter-total-amount-content';
const TOTAL_AMOUNT_ERROR_MESSAGE_ID = 'order-filter-total-amount-error-message';

const MIN_TOTAL_AMOUNT = -1_000_000;
const MAX_TOTAL_AMOUNT = 1_000_000;
const OUT_OF_RANGE_ERROR_MESSAGE =
  'Enter a valid range between $-1,000,000 to $1,000,000';

const isOutOfRange = (value: number | null) =>
  value !== null && (value < MIN_TOTAL_AMOUNT || value > MAX_TOTAL_AMOUNT);

const getOutOfRangeErrorMessage = (min: number | null, max: number | null) =>
  isOutOfRange(min) || isOutOfRange(max) ? OUT_OF_RANGE_ERROR_MESSAGE : '';

// A number input's `.value` (and therefore `Number(.value)`) is only ever a
// valid numeric string or "" — the browser silently substitutes "" whenever
// what's currently typed doesn't parse as a number (e.g. "12-"), even though
// the field keeps *displaying* the raw text. That state is only detectable
// via the native `validity.badInput` flag on the change event, which we
// track per field since the resulting amount looks identical to "empty".
const isInvalidNumber = (value: number | null) =>
  value !== null && !Number.isFinite(value);

const getInvalidAmountErrorMessage = (
  isMinInvalid: boolean,
  isMaxInvalid: boolean,
) => {
  if (isMinInvalid && isMaxInvalid) {
    return 'Enter valid minimum and maximum amounts';
  }
  if (isMinInvalid) {
    return 'Enter a valid minimum amount';
  }
  if (isMaxInvalid) {
    return 'Enter a valid maximum amount';
  }
  return '';
};

const getRelationalErrorMessage = (
  min: number | null,
  max: number | null,
  lastEditedField: 'min' | 'max' | null,
) => {
  const minExceedsMax = min !== null && max !== null && min > max;
  if (!minExceedsMax) {
    return '';
  }
  return lastEditedField === 'max'
    ? 'The maximum amount cannot be less than the minimum amount'
    : 'The minimum value cannot be greater than the maximum amount';
};

const getAmountsError = (
  min: number | null,
  max: number | null,
  lastEditedField: 'min' | 'max' | null,
  isMinInputInvalid: boolean,
  isMaxInputInvalid: boolean,
) =>
  getInvalidAmountErrorMessage(
    isMinInputInvalid || isInvalidNumber(min),
    isMaxInputInvalid || isInvalidNumber(max),
  ) ||
  getOutOfRangeErrorMessage(min, max) ||
  getRelationalErrorMessage(min, max, lastEditedField);

const getRequiredErrorMessage = (min: number | null, max: number | null) => {
  if (min === null && max !== null) {
    return 'Minimum amount is required';
  }
  if (max === null && min !== null) {
    return 'Maximum amount is required';
  }
  return '';
};

interface OrderTotalAmountFilterProps {
  minAmount: number | null;
  maxAmount: number | null;
  setMinAmount: (value: number | null) => void;
  setMaxAmount: (value: number | null) => void;
  totalAmountRangeError: string;
  setTotalAmountRangeError: (value: string) => void;
  setAppliedMinAmount: (value: number | null) => void;
  setAppliedMaxAmount: (value: number | null) => void;
  setFilterChanged: React.Dispatch<React.SetStateAction<boolean>>;
}

export const OrderTotalAmountFilter: React.FC<OrderTotalAmountFilterProps> = ({
  minAmount,
  maxAmount,
  setMinAmount,
  setMaxAmount,
  totalAmountRangeError,
  setTotalAmountRangeError,
  setAppliedMinAmount,
  setAppliedMaxAmount,
  setFilterChanged,
}) => {
  const [lastEditedField, setLastEditedField] = useState<'min' | 'max' | null>(
    null,
  );
  const [isMinInputInvalid, setIsMinInputInvalid] = useState(false);
  const [isMaxInputInvalid, setIsMaxInputInvalid] = useState(false);

  const validateAmounts = () => {
    setTotalAmountRangeError(
      getAmountsError(
        minAmount,
        maxAmount,
        lastEditedField,
        isMinInputInvalid,
        isMaxInputInvalid,
      ),
    );
  };

  const handleAmountChange =
    (
      setAmount: (value: number | null) => void,
      setInputInvalid: (value: boolean) => void,
      field: 'min' | 'max',
    ) =>
    (event: {
      target: { value: string; validity?: { badInput?: boolean } };
    }) => {
      setInputInvalid(Boolean(event.target.validity?.badInput));

      const value =
        event.target.value === '' ? null : Number(event.target.value);

      setAmount(value);
      setLastEditedField(field);
      setTotalAmountRangeError('');
    };

  const hasAmountError = Boolean(totalAmountRangeError);

  return (
    <div className="sui-relative sui-pt-[14px]">
      <Accordion noBorders>
        <AccordionHeader aria-controls={TOTAL_AMOUNT_ACCORDION_CONTENT_ID}>
          Total Amount
        </AccordionHeader>
        <AccordionBody id={TOTAL_AMOUNT_ACCORDION_CONTENT_ID}>
          <div className="sui-mb-[20px]">
            <Typography weight="bold" className="sui-mb-[4px]">
              Enter a custom range:
            </Typography>
          </div>

          <div className="sui-flex sui-items-end sui-gap-[12px] sui-w-full">
            <TextField
              type="number"
              aria-label="Minimum Total Amount"
              placeholder="$ Min"
              fullWidth
              value={minAmount ?? ''}
              status={hasAmountError ? 'error' : undefined}
              InputProps={{
                'aria-describedby': hasAmountError
                  ? TOTAL_AMOUNT_ERROR_MESSAGE_ID
                  : undefined,
              }}
              onChange={handleAmountChange(
                setMinAmount,
                setIsMinInputInvalid,
                'min',
              )}
              onBlur={validateAmounts}
            />
            <div className="sui-flex sui-items-center sui-self-stretch">
              <Typography> to</Typography>
            </div>
            <TextField
              type="number"
              aria-label="Maximum Total Amount"
              placeholder="$ Max"
              fullWidth
              value={maxAmount ?? ''}
              status={hasAmountError ? 'error' : undefined}
              InputProps={{
                'aria-describedby': hasAmountError
                  ? TOTAL_AMOUNT_ERROR_MESSAGE_ID
                  : undefined,
              }}
              onChange={handleAmountChange(
                setMaxAmount,
                setIsMaxInputInvalid,
                'max',
              )}
              onBlur={validateAmounts}
            />
            <Button
              variant="tertiary"
              onClick={() => {
                // Checked first, ahead of the "both empty" bail-out below:
                // a field can hold unparseable text (isMinInputInvalid /
                // isMaxInputInvalid) while its numeric amount reads as null,
                // which must not be silently treated as "nothing entered."
                const amountsError = getAmountsError(
                  minAmount,
                  maxAmount,
                  lastEditedField,
                  isMinInputInvalid,
                  isMaxInputInvalid,
                );
                if (amountsError) {
                  setTotalAmountRangeError(amountsError);
                  return;
                }

                const isMinFilled = minAmount !== null;
                const isMaxFilled = maxAmount !== null;

                if (!isMinFilled && !isMaxFilled) {
                  return;
                }

                const requiredErrorMessage = getRequiredErrorMessage(
                  minAmount,
                  maxAmount,
                );
                if (requiredErrorMessage) {
                  setTotalAmountRangeError(requiredErrorMessage);
                  return;
                }

                setAppliedMinAmount(minAmount);
                setAppliedMaxAmount(maxAmount);
                setFilterChanged(true);
              }}
            >
              Apply
            </Button>
          </div>

          {hasAmountError && (
            <FormStatusMessage
              id={TOTAL_AMOUNT_ERROR_MESSAGE_ID}
              status="error"
            >
              {totalAmountRangeError}
            </FormStatusMessage>
          )}
        </AccordionBody>
      </Accordion>
    </div>
  );
};
